import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import adminApiService from '../services/api';
import {
  DashboardQueues,
  AcquisitionMetrics,
  ChatMetrics,
  ResourceMetrics,
  RecentChatSummary,
  LatestUserSummary,
} from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function DashboardPage() {
  const [queues, setQueues] = useState<DashboardQueues | null>(null);
  const [acquisition, setAcquisition] = useState<AcquisitionMetrics | null>(null);
  const [chats, setChats] = useState<ChatMetrics | null>(null);
  const [resources, setResources] = useState<ResourceMetrics | null>(null);
  const [recentChats, setRecentChats] = useState<RecentChatSummary[]>([]);
  const [latestUsers, setLatestUsers] = useState<LatestUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState(30000); // 30 seconds default

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const [
        queuesData,
        acquisitionData,
        chatsData,
        resourcesData,
        recentChatsData,
        latestUsersData,
      ] = await Promise.all([
        adminApiService.getQueueMetrics(),
        adminApiService.getAcquisitionMetrics(),
        adminApiService.getChatMetrics(),
        adminApiService.getResourceMetrics(),
        adminApiService.getRecentChats(10),
        adminApiService.getLatestUsers(10),
      ]);

      setQueues(queuesData);
      setAcquisition(acquisitionData);
      setChats(chatsData);
      setResources(resourcesData);
      setRecentChats(recentChatsData);
      setLatestUsers(latestUsersData);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Set up polling
    const interval = setInterval(fetchDashboardData, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval]);

  if (loading && !queues) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error: {error}</div>
        <button
          onClick={fetchDashboardData}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center space-x-4">
          <label className="text-sm text-gray-600">
            Auto-refresh:
            <select
              value={pollInterval}
              onChange={(e) => setPollInterval(Number(e.target.value))}
              className="ml-2 border border-gray-300 rounded px-2 py-1"
            >
              <option value={10000}>10 seconds</option>
              <option value={30000}>30 seconds</option>
              <option value={60000}>1 minute</option>
              <option value={300000}>5 minutes</option>
              <option value={0}>Off</option>
            </select>
          </label>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Queue Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <QueueCard
          title="Claude Processing"
          value={queues?.claude.processing || 0}
          subtitle="Currently processing"
        />
        <QueueCard
          title="Claude Pending"
          value={queues?.claude.pending || 0}
          subtitle="Waiting in queue"
        />
        <QueueCard
          title="Inference Processing"
          value={queues?.inference.processing || 0}
          subtitle="Currently processing"
        />
        <QueueCard
          title="Inference Pending"
          value={queues?.inference.pending || 0}
          subtitle="Waiting in queue"
        />
      </div>

      {/* Queue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QueueChart
          title="Claude Jobs"
          completed={queues?.claude.completedPerHour || []}
          failed={queues?.claude.failedPerHour || []}
        />
        <QueueChart
          title="Inference Jobs"
          completed={queues?.inference.completedPerHour || []}
          failed={queues?.inference.failedPerHour || []}
        />
      </div>

      {/* Acquisition Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Acquisition Metrics</h2>
        <AcquisitionChart data={acquisition} />
      </div>

      {/* Recent chats from Mongo */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Last 10 chats</h2>
        <RecentChatsTable chats={recentChats} />
      </div>

      {/* Latest users (Postgres + machine_ids) */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Latest 10 users</h2>
        <LatestUsersTable users={latestUsers} />
      </div>

      {/* Chat Metrics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Chat Metrics</h2>
        <ChatChart data={chats} />
      </div>

      {/* Resource Metrics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Resource Usage</h2>
        <ResourceCharts data={resources} />
      </div>
    </div>
  );
}

function QueueCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: number;
  subtitle: string;
}) {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-sm font-medium text-gray-600">{title}</h3>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
    </div>
  );
}

function QueueChart({
  title,
  completed,
  failed,
}: {
  title: string;
  completed: Array<{ hour: string; count: number }>;
  failed: Array<{ hour: string; count: number }>;
}) {
  const labels = completed.map((item) => new Date(item.hour).toLocaleString());
  const completedData = completed.map((item) => item.count);
  const failedData = failed.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Completed',
        data: completedData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Failed',
        data: failedData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <Line data={chartData} options={options} />
    </div>
  );
}

function acquisitionDateLabels(data: AcquisitionMetrics): string[] {
  const set = new Set<string>();
  data.signupsPerDay.forEach((x) => set.add(x.date));
  data.conversionsPerDay.forEach((x) => set.add(x.date));
  (data.newMachineIdsPerDay || []).forEach((x) => set.add(x.date));
  return Array.from(set).sort();
}

function seriesCountsForLabels(
  labels: string[],
  series: Array<{ date: string; count: number }>
): number[] {
  const map = new Map(series.map((x) => [x.date, x.count]));
  return labels.map((d) => map.get(d) ?? 0);
}

function AcquisitionChart({ data }: { data: AcquisitionMetrics | null }) {
  if (!data) return <div>No data available</div>;

  const labels = acquisitionDateLabels(data);
  const signupsData = seriesCountsForLabels(labels, data.signupsPerDay);
  const conversionsData = seriesCountsForLabels(labels, data.conversionsPerDay);
  const newMachinesData = seriesCountsForLabels(
    labels,
    data.newMachineIdsPerDay || []
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Signups',
        data: signupsData,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Conversions',
        data: conversionsData,
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'New machine_ids (first user message)',
        data: newMachinesData,
        borderColor: 'rgb(234, 88, 12)',
        backgroundColor: 'rgba(234, 88, 12, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function ChatChart({ data }: { data: ChatMetrics | null }) {
  if (!data) return <div>No data available</div>;

  const labels = data.chatsPerDay.map((item) => item.date);
  const chatsData = data.chatsPerDay.map((item) => item.count);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Chats per Day',
        data: chatsData,
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

function ResourceCharts({ data }: { data: ResourceMetrics | null }) {
  if (!data) return <div>No data available</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Token Usage</h3>
        <TokenUsageChart data={data.tokensPerDay} />
      </div>
    </div>
  );
}

function LatestUsersTable({ users }: { users: LatestUserSummary[] }) {
  if (!users.length) {
    return <p className="text-sm text-gray-500">No users yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-600">
            <th className="py-2 pr-4 font-medium">Signed up</th>
            <th className="py-2 pr-4 font-medium">Email</th>
            <th className="py-2 font-medium">machine_id</th>
          </tr>
        </thead>
        <tbody>
          {users.map((row) => (
            <tr key={row.userId} className="border-b border-gray-100 align-top">
              <td className="py-3 pr-4 whitespace-nowrap text-gray-700">
                {new Date(row.createdAt).toLocaleString()}
              </td>
              <td className="py-3 pr-4">
                <Link
                  to={`/users/${row.userId}`}
                  className="text-blue-700 hover:text-blue-900 hover:underline break-all"
                >
                  {row.email ?? '—'}
                </Link>
              </td>
              <td className="py-3 font-mono text-xs text-gray-800 break-all max-w-md">
                {row.machineId ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentChatsTable({ chats }: { chats: RecentChatSummary[] }) {
  if (!chats.length) {
    return <p className="text-sm text-gray-500">No chats yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-600">
            <th className="py-2 pr-4 font-medium">Last activity</th>
            <th className="py-2 pr-4 font-medium">machine_id</th>
            <th className="py-2 font-medium">First user message</th>
          </tr>
        </thead>
        <tbody>
          {chats.map((row) => (
            <tr key={row.chatId} className="border-b border-gray-100 align-top">
              <td className="py-3 pr-4 whitespace-nowrap text-gray-700">
                {new Date(row.lastUpdated).toLocaleString()}
              </td>
              <td className="py-3 pr-4 font-mono text-xs text-gray-800 break-all max-w-[200px]">
                {row.machineId ?? '—'}
              </td>
              <td className="py-3">
                <Link
                  to={`/chats/${encodeURIComponent(row.chatId)}`}
                  className="text-left text-blue-700 hover:text-blue-900 hover:underline block max-w-2xl"
                >
                  {row.firstUserMessagePreview || '(empty)'}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TokenUsageChart({
  data,
}: {
  data: ResourceMetrics['tokensPerDay'];
}) {
  const labels = data.input.map((item) => item.date);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Input Tokens',
        data: data.input.map((item) => item.count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Output Tokens',
        data: data.output.map((item) => item.count),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Cache Read',
        data: data.cacheRead.map((item) => item.count),
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.1,
      },
      {
        label: 'Cache Write',
        data: data.cacheWrite.map((item) => item.count),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return <Line data={chartData} options={options} />;
}
